import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getSellerPath } from "@/lib/routing/portal-urls";
import { useSwitchStoreMutation } from "@/shared/services/useSwitchStoreMutation";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";

type UseStoreSwitchResult = {
  isSwitching: boolean;
  error: string | null;
  switchTo: (nextStoreSlug: string) => Promise<void>;
};

/**
 * Shared store-switch logic — single source of truth extracted from
 * `AccountPage.handleStoreSwitch`. Consumed by the Account page (Phase 1) and,
 * later, the sidebar / login picker / mobile switchers.
 *
 * `activeStoreSlug` is the currently-active store (from the session). `switchTo`
 * no-ops when the target is empty, already the active slug, or a switch is
 * already in flight — matching AccountPage's original guard exactly.
 *
 * On success it calls `switchStore` (which invalidates `["seller"]`) then
 * navigates to the target store (replace). On failure it surfaces the
 * ApiJsonClientError message, falling back to the same i18n error key
 * AccountPage used previously.
 */
export function useStoreSwitch(
  locale: string,
  activeStoreSlug: string,
): UseStoreSwitchResult {
  const t = useTranslations();
  const navigate = useNavigate();
  const { mutateAsync: switchSellerStore } = useSwitchStoreMutation();

  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const switchTo = useCallback(
    async (nextStoreSlug: string) => {
      if (
        !nextStoreSlug ||
        nextStoreSlug === activeStoreSlug ||
        isSwitching
      ) {
        return;
      }
      setError(null);
      setIsSwitching(true);
      try {
        await switchSellerStore({ storeSlug: nextStoreSlug });
        navigate(getSellerPath(locale, nextStoreSlug), { replace: true });
      } catch (err) {
        if (err instanceof ApiJsonClientError) {
          setError(err.message || t(keys.seller.storeSwitcherError));
        } else {
          setError(t(keys.seller.storeSwitcherError));
        }
      } finally {
        setIsSwitching(false);
      }
    },
    [activeStoreSlug, isSwitching, locale, navigate, switchSellerStore, t],
  );

  return { isSwitching, error, switchTo };
}
